import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtCurrency, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Download,
  Upload,
  Wand2,
  Database,
  X,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY = { sku: "", name: "", default_cost: 0, supplier: "", category: "cabinet", notes: "", consumables: [] };

export default function CostCatalog() {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]); // unfiltered for KPI tiles
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // product object or null
  const [bootstrapping, setBootstrapping] = useState(false);
  const [applying, setApplying] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const [{ data: filtered }, { data: full }] = await Promise.all([
      api.get("/products", { params: q ? { q } : {} }),
      q ? api.get("/products") : Promise.resolve({ data: null }),
    ]);
    setItems(filtered);
    setAllItems(full || filtered);
  };

  useEffect(() => { load(); }, [q]);

  const summary = useMemo(() => {
    const list = allItems;
    const total = list.length;
    const priced = list.filter((p) => (p.resolved_cost || p.default_cost) > 0).length;
    return { total, priced, missing: total - priced };
  }, [allItems]);

  const remove = async (sku) => {
    if (!window.confirm(`Remove ${sku} from catalog?`)) return;
    try {
      await api.delete(`/products/${encodeURIComponent(sku)}`);
      toast.success("Removed");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const bootstrap = async () => {
    if (!window.confirm("Create catalog stubs for every SKU found in current orders? Existing SKUs are skipped.")) return;
    setBootstrapping(true);
    try {
      const { data } = await api.post("/products/bootstrap-from-orders");
      toast.success(`Added ${data.inserted} new SKUs from orders`);
      load();
    } catch {
      toast.error("Bootstrap failed");
    } finally {
      setBootstrapping(false);
    }
  };

  const apply = async () => {
    if (!window.confirm("Apply current catalog costs to every order line item? Manual overrides on items are preserved.")) return;
    setApplying(true);
    try {
      const { data } = await api.post("/products/apply-to-orders");
      toast.success(`Updated ${data.updated_items} items across ${data.updated_orders} orders`);
    } catch {
      toast.error("Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const exportCsv = () => {
    window.open(`${API_BASE}/products.csv`, "_blank");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    const rows = text.split(/\r?\n/).filter(Boolean);
    const header = rows.shift().split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
    const idx = (col) => header.indexOf(col);
    const parsed = rows.map((line) => {
      // naive CSV split, handles quoted commas
      const cells = [];
      let cur = "", inQ = false;
      for (const ch of line) {
        if (ch === '"') inQ = !inQ;
        else if (ch === "," && !inQ) { cells.push(cur); cur = ""; }
        else cur += ch;
      }
      cells.push(cur);
      const get = (col) => (idx(col) >= 0 ? (cells[idx(col)] || "").replace(/^"|"$/g, "").trim() : "");
      const consRaw = get("consumables");
      const consumables = consRaw
        ? consRaw.split("|").map((c) => {
            const [sku, qty] = c.split("x");
            return sku ? { sku: sku.trim(), qty: parseFloat(qty || "1") } : null;
          }).filter(Boolean)
        : [];
      return {
        sku: get("sku"),
        name: get("name") || get("sku"),
        default_cost: parseFloat(get("default_cost") || "0") || 0,
        supplier: get("supplier") || null,
        category: get("category") || "cabinet",
        notes: get("notes") || null,
        consumables,
      };
    }).filter((p) => p.sku);
    try {
      const { data } = await api.post("/products/bulk", parsed);
      toast.success(`Imported ${data.inserted + data.updated} rows (${data.inserted} new, ${data.updated} updated)`);
      load();
    } catch {
      toast.error("Import failed");
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1500px] animate-fade-in">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="label-eyebrow">Cost catalog</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">
            Every SKU, every cost
          </h1>
          <p className="text-muted-foreground mt-1">
            From base cabinets to a single screw — set the cost once and every order&apos;s profit becomes accurate.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input ref={fileRef} type="file" accept=".csv" onChange={onFile} className="hidden" data-testid="catalog-csv-input" />
          <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="catalog-import">
            <Upload className="w-4 h-4 mr-2" /> Import CSV
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="catalog-export">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={bootstrap}
            disabled={bootstrapping}
            data-testid="catalog-bootstrap"
          >
            <Database className="w-4 h-4 mr-2" /> Sync from orders
          </Button>
          <Button
            onClick={apply}
            disabled={applying}
            data-testid="catalog-apply"
            className="bg-primary hover:bg-[#203A2C]"
          >
            <Wand2 className="w-4 h-4 mr-2" /> Apply to orders
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="stat-card">
          <div className="label-eyebrow">Total SKUs</div>
          <div className="mt-2 font-heading text-3xl font-bold num">{summary.total}</div>
        </div>
        <div className="stat-card">
          <div className="label-eyebrow">Priced</div>
          <div className="mt-2 font-heading text-3xl font-bold num text-primary">{summary.priced}</div>
        </div>
        <div className="stat-card">
          <div className="label-eyebrow">Needs cost</div>
          <div className="mt-2 font-heading text-3xl font-bold num text-[#a44231]">{summary.missing}</div>
        </div>
      </div>

      <div className="surface-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="catalog-search"
              placeholder="Search SKU or name (e.g. HW-PULL, screw, hinge)"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
            <DialogTrigger asChild>
              <Button
                data-testid="catalog-new"
                onClick={() => setEditing({ ...EMPTY })}
                className="bg-primary hover:bg-[#203A2C]"
              >
                <Plus className="w-4 h-4 mr-2" /> New SKU
              </Button>
            </DialogTrigger>
            <ProductDialog
              product={editing}
              onClose={() => setEditing(null)}
              onSaved={() => { setEditing(null); load(); }}
            />
          </Dialog>
        </div>

        <div className="overflow-x-auto -mx-4 sm:-mx-6">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/70">
                <th className="text-left font-medium px-4 sm:px-6 py-3">SKU</th>
                <th className="text-left font-medium px-4 py-3">Name</th>
                <th className="text-left font-medium px-4 py-3 hidden md:table-cell">Supplier</th>
                <th className="text-left font-medium px-4 py-3 hidden lg:table-cell">Consumables</th>
                <th className="text-right font-medium px-4 py-3">Base cost</th>
                <th className="text-right font-medium px-4 py-3">Total cost</th>
                <th className="text-right font-medium px-4 sm:px-6 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.sku} className="border-b border-border/40 hover:bg-secondary/40 transition-colors">
                  <td className="px-4 sm:px-6 py-3 font-mono text-xs">{p.sku}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{p.name}</div>
                    {p.notes && <div className="text-[11px] text-muted-foreground truncate max-w-md">{p.notes}</div>}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground hidden md:table-cell">{p.supplier || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                    {(p.consumables || []).length === 0 ? "—" : (
                      <span>{p.consumables.length} part{p.consumables.length === 1 ? "" : "s"}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right num">{p.default_cost ? fmtCurrency(p.default_cost) : <span className="text-[#a44231]">—</span>}</td>
                  <td className="px-4 py-3 text-right num font-semibold">
                    {(p.resolved_cost || p.default_cost) > 0
                      ? fmtCurrency(p.resolved_cost || p.default_cost)
                      : <span className="text-[#a44231]">—</span>}
                  </td>
                  <td className="px-4 sm:px-6 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing({ ...EMPTY, ...p })}
                        data-testid={`catalog-edit-${p.sku}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => remove(p.sku)}
                        data-testid={`catalog-del-${p.sku}`}
                      >
                        <Trash2 className="w-4 h-4 text-[#a44231]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-muted-foreground py-10">
                    No products yet. Click &ldquo;Sync from orders&rdquo; to bootstrap from your live order history.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function ProductDialog({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product || EMPTY);

  useEffect(() => { setForm(product || EMPTY); }, [product]);

  if (!product) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addConsumable = () => set("consumables", [...(form.consumables || []), { sku: "", qty: 1 }]);
  const updateCons = (i, k, v) => {
    const next = [...(form.consumables || [])];
    next[i] = { ...next[i], [k]: v };
    set("consumables", next);
  };
  const removeCons = (i) => set("consumables", form.consumables.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!form.sku) { toast.error("SKU required"); return; }
    try {
      await api.post("/products", {
        sku: form.sku,
        name: form.name || form.sku,
        default_cost: parseFloat(form.default_cost) || 0,
        supplier: form.supplier || null,
        category: form.category || "cabinet",
        notes: form.notes || null,
        consumables: (form.consumables || [])
          .filter((c) => c.sku)
          .map((c) => ({ sku: c.sku, qty: parseFloat(c.qty) || 1 })),
      });
      toast.success("Saved");
      onSaved();
    } catch {
      toast.error("Save failed");
    }
  };

  return (
    <DialogContent className="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{form.sku ? `Edit ${form.sku}` : "New SKU"}</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label>SKU</Label>
            <Input
              value={form.sku}
              onChange={(e) => set("sku", e.target.value)}
              disabled={!!product.sku}
              data-testid="product-sku"
            />
          </div>
          <div>
            <Label>Name</Label>
            <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name" />
          </div>
          <div>
            <Label>Base cost</Label>
            <Input
              type="number"
              step="0.0001"
              value={form.default_cost}
              onChange={(e) => set("default_cost", e.target.value)}
              data-testid="product-cost"
            />
          </div>
          <div>
            <Label>Category</Label>
            <Input value={form.category || ""} onChange={(e) => set("category", e.target.value)} placeholder="cabinet / hardware / appliance" data-testid="product-category" />
          </div>
          <div className="sm:col-span-2">
            <Label>Supplier</Label>
            <Input value={form.supplier || ""} onChange={(e) => set("supplier", e.target.value)} data-testid="product-supplier" />
          </div>
          <div className="sm:col-span-2">
            <Label>Notes</Label>
            <Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Optional notes" data-testid="product-notes" />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="m-0">Consumables (e.g. screws, glue, hinges used per unit)</Label>
            <Button size="sm" variant="ghost" onClick={addConsumable} data-testid="product-add-consumable">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
          {(form.consumables || []).length === 0 && (
            <div className="text-xs text-muted-foreground italic">No consumables. Add screws, hinges, glue, etc. to roll their cost into this SKU&apos;s total cost.</div>
          )}
          {(form.consumables || []).map((c, i) => (
            <div key={i} className="flex gap-2 mb-2 items-center">
              <Input
                placeholder="Consumable SKU (e.g. HW-SCREW-1)"
                value={c.sku}
                onChange={(e) => updateCons(i, "sku", e.target.value)}
                className="flex-1"
                data-testid={`consumable-sku-${i}`}
              />
              <Input
                type="number"
                step="0.01"
                placeholder="Qty"
                value={c.qty}
                onChange={(e) => updateCons(i, "qty", e.target.value)}
                className="w-24"
                data-testid={`consumable-qty-${i}`}
              />
              <Button size="sm" variant="ghost" onClick={() => removeCons(i)} data-testid={`consumable-remove-${i}`}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={save} className="bg-primary hover:bg-[#203A2C]" data-testid="product-save">Save</Button>
      </DialogFooter>
    </DialogContent>
  );
}
